/**
 * Section 12g — Golden Signals.
 *
 * The per-user library of finalised signals, living as the third tab on the
 * Strategies hub. Documented against the shipped code: the seeded BW entry's
 * numbers are the ones verifyGoldenSignals pins to BW_DEFAULTS, and the
 * `?overlays=` behaviour described here is what ChartsPage actually does.
 */
import React from 'react';
import {
  Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText,
} from '@mui/material';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import GuideCallout from '../GuideCallout';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);
const Li = ({ children }: { children: React.ReactNode }) => (
  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
    <ListItemText primary={children} />
  </ListItem>
);

const Section_goldenSignals: GuideSection = {
  id: 'golden-signals',
  label: 'Golden Signals',
  group: 'pages',
  icon: <BookmarksIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Golden Signals</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        A permanent library of the signals you have finalised. Each entry spells out the exact conditions
        that produce it — the indicators it needs, every entry and exit rule, the parameter values and where
        they live, what it measured, and what you tried that did <em>not</em> work — so a combination you
        find on the Charts page is recorded instead of lost, and a strategy can later be built out of one
        or several of them. It is the third tab on{' '}
        <OpenInApp to="/golden-signals" label="Strategies → Golden Signals" />.
      </Typography>

      <GuideCallout kind="warning" title="This page is a catalogue">
        Golden Signals places no orders, runs no scans and arms nothing. It sits next to the Strategies tab,
        whose cards read ACTIVE / PAUSED and really do trade — Golden Signals only describes. There is no
        Run, no Execute and no Activate here, and the API behind it exposes none.
      </GuideCallout>

      <H id="what-an-entry-holds">What an entry holds</H>
      <P>
        Entries are structured rather than free text, which is what makes them worth having later: a rule
        written as prose has to be re-read and re-interpreted every time, while a rule written as rows can
        be compared, filtered, and eventually composed.
      </P>
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Section</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>What goes in it</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell><strong>Name, direction, status, tags</strong></TableCell>
            <TableCell>
              Direction is LONG / SHORT / BOTH / CONTEXT — CONTEXT means a condition that gates other
              signals rather than a trade of its own. Status is DRAFT / TESTING / CONFIRMED / RETIRED.
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>What it needs</strong></TableCell>
            <TableCell>
              The chart chips that have to be on, the timeframes it was measured on (and the ones it is
              weak on), and the markets it was tested in.
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>The mark</strong></TableCell>
            <TableCell>The label, colour, shape and position of what the chart actually draws.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>Entry / exit conditions</strong></TableCell>
            <TableCell>
              One row per condition, each with a short tag and a full explanation — including the
              qualifiers that are easy to forget, like "closed bars only" or "one mark per episode".
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>Parameters</strong></TableCell>
            <TableCell>
              The value <em>and where in the app that number lives</em>, such as
              <code> settings.bbr.osLevel</code>. A value with no source is a value nobody can check.
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>Evidence</strong></TableCell>
            <TableCell>
              What it measured, on what dataset, against what baseline. The baseline is the part that makes
              a number mean something.
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>What did not work</strong></TableCell>
            <TableCell>
              Variants measured dead. This is what stops you re-testing the same idea in six months.
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>Related &amp; defined in</strong></TableCell>
            <TableCell>
              Strategies the signal leads to, and the repo files that define it.
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <GuideCallout kind="tip" title="RETIRED is a keeper, not a delete">
        When something stops working, set its status to RETIRED rather than deleting it. Knowing what failed
        — and what it measured when it failed — is worth as much as knowing what worked. The delete
        confirmation says the same thing.
      </GuideCallout>

      <H id="using-the-page">Using the page</H>
      <List dense sx={{ pl: 1 }}>
        <Li><strong>Search</strong> looks inside the whole entry, not just the name — rules, evidence and tags included. The thing you remember about a signal months later is usually a phrase from its rules.</Li>
        <Li><strong>New signal</strong> opens the structured editor. Entry conditions, exit conditions, parameters and evidence are repeatable rows: add, reorder and remove them independently.</Li>
        <Li><strong>Duplicate</strong> copies an entry as a fresh DRAFT — deliberately, because the evidence on the original was measured for the original rule, not for whatever the copy becomes.</Li>
        <Li><strong>Restore shipped text</strong> appears only on entries that shipped with the app. It replaces your edits with the shipped version.</Li>
      </List>

      <H id="show-me-on-a-chart">"Show me this on a chart"</H>
      <P>
        Each entry can carry a symbol, interval, candle type and the list of indicator chips it needs. The
        link on the entry opens exactly that chart and <strong>switches those chips on</strong>. It never
        switches anything off.
      </P>
      <GuideCallout kind="info" title="It becomes your remembered chart setup">
        Turning chips on this way is the same as clicking them yourself, so it is remembered as your chart
        setup afterwards — exactly like following a link that sets the interval or candle type. If you want
        the old set back, toggle the chips off again.
      </GuideCallout>

      <H id="bw">The first entry — BW</H>
      <P>
        The library ships with one entry, the <strong>BW long</strong>: the green up arrow marked{' '}
        <code>BW</code> that appears below a candle when the BBR or Wave oscillator chip is on. It is
        seeded into your library and is then an ordinary entry — edit it, annotate it, retag it. The
        shipped text is always one click away.
      </P>
      <P>
        Its rule, as the chart draws it: <strong>BBR at or below −100</strong> and{' '}
        <strong>Wave (wt1) at or below −60</strong>, with <strong>BBR rising against the previous bar</strong>.
        Evaluated on the last closed bar, marked once per oversold episode. The paired <code>X</code> above
        a later candle closes it — whichever comes first of a 2.5 × ATR stop (amber), BBR returning to the
        midline 0 (blue), or 20 bars (grey).
      </P>
      <GuideCallout kind="info" title="Where those two levels actually live">
        −100 and −60 come from the <em>indicators'</em> own settings — Chart Settings → BBR → oversold level,
        and Chart Settings → Wave → oversold level — not from the BBR × Wave section. That section owns
        the turn requirement, the exit level, the stop and the bar cap. The entry's Parameters list names
        the path for each one.
      </GuideCallout>
      <P>
        The two levels line up on screen because each oscillator is drawn on its own scale, pinned to 1.5×
        its own overbought level. They are equivalent in <em>rarity</em> — each tags about 5.8% of bars —
        not in value. Wave never reaches −100 at all, which is why "BBR crosses Wave below the oversold
        line", the thing the pattern looks like, cannot actually happen.
      </P>
      <P>
        Full detail, including the measurements and the four variants that were tried and measured dead,
        is on the entry itself. See also{' '}
        <OpenInApp to="/app-guide#indicators" label="Indicators" /> for BBR and Wave, and{' '}
        <OpenInApp to="/app-guide#strategy-vajra" label="Vajra Strategy" /> for what this same rule looks
        like once a higher-timeframe gate is added to it.
      </P>
    </>
  ),
  searchIndex: [
    {
      heading: 'Golden Signals library',
      anchor: 'what-an-entry-holds',
      body: 'golden signals library catalogue signal entries record finalised signals conditions entry exit parameters evidence tags status direction draft testing confirmed retired',
    },
    {
      heading: 'Using the Golden Signals page',
      anchor: 'using-the-page',
      body: 'new signal editor duplicate delete restore shipped text search filter status tag repeatable rows structured editor',
    },
    {
      heading: 'Show a signal on a chart',
      anchor: 'show-me-on-a-chart',
      body: 'open on chart overlays parameter turn indicators on chips symbol interval candle remembered chart setup',
    },
    {
      heading: 'The BW signal',
      anchor: 'bw',
      body: 'BW signal BBR wave oversold turn -100 -60 conjunction exit midline ATR stop maxHold 20 bars vajra arrow mark green up arrow',
    },
  ],
};

export default Section_goldenSignals;
